import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CellWrapper, CheckboxHeaderWrapper, StyledCheckbox, Hint } from '../styles';

const CheckboxHeaderCell = ({
  setSelectedRows,
  isAllSelected,
  allRowIndices,
  isActive = false,
  isEditing = false,
  rowIndex,
  colIndex,
  registerCellRef,
}) => {
  const [showHint, setShowHint] = useState(false);
  const cellRef = useRef(null);

  useEffect(() => {
    if (registerCellRef && typeof rowIndex === 'number' && typeof colIndex === 'number') {
      registerCellRef(rowIndex, colIndex, cellRef.current);

      return () => registerCellRef(rowIndex, colIndex, null);
    }

    return undefined;
  }, [registerCellRef, rowIndex, colIndex]);

  useEffect(() => {
    if (!isActive) return undefined;

    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();

        if (isAllSelected) {
          setSelectedRows([]);
        } else {
          setSelectedRows(allRowIndices);
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [isActive, isAllSelected, setSelectedRows, allRowIndices]);

  const handleClick = () => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allRowIndices);
    }
  };

  return (
    <CellWrapper
      ref={cellRef}
      isActive={isActive}
      isEditing={isEditing}
      onClick={handleClick}
      tabIndex={isActive ? 0 : -1}
      role="gridcell"
      aria-label={`Select all checkbox, ${isAllSelected ? 'all selected' : 'none selected'}`}
    >
      <CheckboxHeaderWrapper
        onMouseEnter={() => setShowHint(true)}
        onMouseLeave={() => setShowHint(false)}
      >
        <StyledCheckbox
          type="checkbox"
          checked={isAllSelected}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows(allRowIndices);
            } else {
              setSelectedRows([]);
            }
          }}
          tabIndex={-1}
        />
        {showHint && (
          <Hint>
            {isAllSelected ? 'Unselect All' : 'Select All'}
          </Hint>
        )}
      </CheckboxHeaderWrapper>
    </CellWrapper>
  );
};

CheckboxHeaderCell.propTypes = {
  setSelectedRows: PropTypes.func.isRequired,
  isAllSelected: PropTypes.bool.isRequired,
  allRowIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
  isActive: PropTypes.bool,
  isEditing: PropTypes.bool,
  rowIndex: PropTypes.number,
  colIndex: PropTypes.number,
  registerCellRef: PropTypes.func,
};

CheckboxHeaderCell.defaultProps = {
  isActive: false,
  isEditing: false,
  rowIndex: undefined,
  colIndex: undefined,
  registerCellRef: undefined,
};

export default CheckboxHeaderCell;
